import { useMutation } from "@tanstack/react-query";
import { useLayoutEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import type { DemoLoginConfirm } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import FormTitle from "~/components/login/formTitle/FormTitle";
import NotificationBlock from "~/components/notificationBlock/NotificationBlock";
import Button from "~/components/ui/button/Button";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import labels from "~/helpers/labels";
import { tryCatch } from "~/helpers/tryCatch";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./Login.module.scss";

const DemoLoginPage = () => {
  usePageTitle(PAGE_TITLES.demoLogin);

  const [searchParams] = useSearchParams();
  // Snapshot the one-time token at mount so the URL can be safely cleaned up
  // below. Any subsequent navigation away from `?token=...` must not lose
  // the value we still need to send to the server.
  const [loginToken] = useState(() => searchParams.get("token"));
  // The token is single-use: once the server rejects it there is nothing to
  // retry, so the page flips to the invalid-link screen for good.
  const [isRejected, setIsRejected] = useState(false);
  // Transient failure (network / 5xx): the token has NOT been consumed, so
  // the page stays on the confirm screen and the user can retry.
  const [isConfirmFailed, setIsConfirmFailed] = useState(false);
  const navigate = useNavigate();
  const { token, user, setSession } = useAuth();

  // Strip `?token=` from the URL before the browser paints so route-mounted
  // analytics SDKs never see the one-time token. Same pattern as UnlockPage —
  // see the detailed rationale there.
  useLayoutEffect(() => {
    if (searchParams.has("token")) {
      navigate(routes.demoLogin(), { replace: true });
    }
    // Intentionally runs once on mount: subsequent param changes are
    // either us replacing it away (no-op) or external — neither warrants
    // re-running this side effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: DemoLoginConfirm) =>
      apiClient.demoLogin.confirmCreate(data),
  });

  if (!loginToken || isRejected) {
    return (
      <>
        <FormTitle title="Invalid Link" />
        <div className={styles.infoText}>This link is invalid or expired.</div>
        <div className={styles.footer}>
          <Link className={styles.footerLink} to={routes.auth.login()}>
            {labels.BACK_TO_LOGIN}
          </Link>
        </div>
      </>
    );
  }

  const onOpen = async () => {
    setIsConfirmFailed(false);

    // The error body is not rendered, so plain `tryCatch` is enough — and it
    // also sidesteps parsing non-JSON proxy error pages.
    const result = await tryCatch(mutateAsync({ login_token: loginToken }));

    if (result.error) {
      // Only a 4xx means the server actually rejected the token. A 5xx or a
      // network failure is transient — the token is still valid, so keep the
      // confirm screen instead of burning it with the invalid-link one.
      if (result.error instanceof Response && result.error.status < 500) {
        setIsRejected(true);
      } else {
        setIsConfirmFailed(true);
      }

      return;
    }

    setSession(result.data.data.access_token);
    // No guard wraps /demo-login (the page must be reachable regardless of
    // the current session), so the success redirect is explicit here.
    // AppGate's MFA gate will still take over if the new session requires it.
    // `replace` drops the tokenless /demo-login entry from history — Back
    // would otherwise land on the invalid-link screen.
    navigate(routes.home(), { replace: true });
  };

  return (
    <>
      <FormTitle title="Your demo platform is ready" />
      <div className={styles.infoText}>
        Everything is set up and ready to explore.
      </div>
      {token && (
        <div className={styles.notificationWrapper}>
          <NotificationBlock type="warning">
            {user?.email
              ? `You are currently signed in as ${user.email}. Continuing will sign you out of that account.`
              : "You are currently signed in. Continuing will sign you out of that account."}
          </NotificationBlock>
        </div>
      )}
      {isConfirmFailed && (
        <div className={styles.notificationWrapper}>
          <NotificationBlock type="danger">
            Something went wrong. Please try again.
          </NotificationBlock>
        </div>
      )}
      <Button
        fullWidth
        onClick={onOpen}
        disabled={isPending}
        withSpinner={isPending}
      >
        Open Dashboard
      </Button>
    </>
  );
};

export default DemoLoginPage;
